import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Link, useNavigate } from "react-router-dom";
import { Upload, FileDown, CheckCircle2, RotateCcw, Trash2, FileText, FilePlus2, MessageSquare, Sparkles } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useApp } from "@/context/AppContext";
import { PageHeader, Panel, Loading, Empty, Field, Th, Td, TableWrap, NativeSelect } from "@/components/kit";
import { dmyTime, todayIso, errMsg } from "@/lib/format";
import { ReceiptScan } from "@/components/ReceiptScan";

const CATS = ["achat", "vente", "banque", "caisse", "social", "fiscal", "autre"];

const UploadPanel = ({ onDone }) => {
  const { api, t } = useApp();
  const ref = useRef();
  const [f, setF] = useState({ category: "achat", month: todayIso().slice(0, 7), note: "" });
  const [busy, setBusy] = useState(false);
  const [drag, setDrag] = useState(false);
  const send = async (files) => {
    setBusy(true);
    let ok = 0;
    for (const file of files) {
      const fd = new FormData();
      fd.append("file", file);
      Object.entries(f).forEach(([k, v]) => fd.append(k, v));
      try { await api.post("/justificatifs", fd); ok += 1; } catch (e) { toast.error(`${file.name} : ${errMsg(e)}`); }
    }
    setBusy(false);
    if (ok) { toast.success(`${ok} ${t("justif_uploaded")}`); setF({ ...f, note: "" }); onDone(); }
  };
  return (
    <Panel title={t("justif_upload")}>
      <div className="grid gap-3 sm:grid-cols-3">
        <Field label={t("justif_category")}><NativeSelect value={f.category} onChange={(e) => setF({ ...f, category: e.target.value })} data-testid="justif-category">{CATS.map((c) => <option key={c} value={c}>{t(`cat_${c}`)}</option>)}</NativeSelect></Field>
        <Field label={t("period")}><Input type="month" value={f.month} onChange={(e) => e.target.value && setF({ ...f, month: e.target.value })} data-testid="justif-month" /></Field>
        <Field label={t("notes")}><Input value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} data-testid="justif-note" /></Field>
      </div>
      <input ref={ref} type="file" multiple accept="image/*,.pdf,.xlsx,.xls,.csv,.doc,.docx" hidden data-testid="justif-file-input"
        onChange={(e) => { send([...e.target.files]); e.target.value = ""; }} />
      <button type="button" disabled={busy} onClick={() => ref.current.click()} data-testid="justif-dropzone"
        onDragOver={(e) => { e.preventDefault(); setDrag(true); }} onDragLeave={() => setDrag(false)}
        onDrop={(e) => { e.preventDefault(); setDrag(false); send([...e.dataTransfer.files]); }}
        className={`mt-4 flex w-full flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed px-6 py-8 text-sm transition-colors ${drag ? "border-teal-600 bg-teal-50" : "border-slate-200 hover:border-teal-400 hover:bg-slate-50"}`}>
        <Upload className="h-6 w-6 text-teal-600" /><span className="font-medium">{busy ? t("loading") : t("justif_drop")}</span><span className="text-xs text-slate-400">PDF, images, Excel — 10 Mo max</span>
      </button>
    </Panel>
  );
};

const CommentsDialog = ({ j, onClose, onSaved }) => {
  const { api, t } = useApp();
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  if (!j) return null;
  const send = async () => {
    setBusy(true);
    try { await api.post(`/justificatifs/${j.id}/comments`, { text }); setText(""); onSaved(); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-lg bg-white">
        <DialogHeader><DialogTitle className="truncate">{t("comments")} · {j.name}</DialogTitle></DialogHeader>
        <div className="max-h-80 space-y-3 overflow-y-auto" data-testid="justif-comments-list">
          {(j.comments || []).length === 0 && <p className="text-sm text-slate-400">{t("empty")}</p>}
          {(j.comments || []).map((c) => (
            <div key={c.id} className={`rounded-lg p-3 text-sm ${c.role === "firm" ? "bg-teal-50" : "bg-slate-50"}`}>
              <div className="mb-1 text-xs text-slate-500"><b className="text-slate-700">{c.name || c.by}</b> · {t(`r_${c.role}`)} · {dmyTime(c.at)}</div>
              <p className="whitespace-pre-wrap">{c.text}</p>
            </div>
          ))}
        </div>
        <Textarea value={text} onChange={(e) => setText(e.target.value)} placeholder={t("comment_placeholder")} data-testid="justif-comment-input" />
        <Button onClick={send} disabled={busy || !text.trim()} data-testid="justif-comment-send">{t("comment_send")}</Button>
      </DialogContent>
    </Dialog>
  );
};

export default function Justificatifs() {
  const { api, t, can, download, me, company } = useApp();
  const [rows, setRows] = useState(null);
  const [month, setMonth] = useState("");
  const [status, setStatus] = useState("");
  const [openId, setOpenId] = useState(null);
  const load = useCallback(() => api.get("/justificatifs", { params: { month: month || undefined, status: status || undefined } }).then((r) => setRows(r.data)), [api, month, status]);
  useEffect(() => { load(); }, [load]);
  const rw = can("justificatifs", true);
  const nav = useNavigate();
  const toDoc = async (j, type, auto_read = false) => {
    const tid = auto_read ? toast.loading(t("ocr_reading")) : undefined;
    try { const { data } = await api.post(`/justificatifs/${j.id}/to-document`, { type, auto_read }); toast.success(t("justif_doc_created"), { id: tid }); nav(`/app/documents/${data.id}/edit`); }
    catch (e) { toast.error(errMsg(e), { id: tid }); }
  };
  const act = async (fn) => { try { await fn(); load(); } catch (e) { toast.error(errMsg(e)); } };
  const newCount = rows?.filter((r) => r.status === "new").length || 0;
  return (
    <>
      <PageHeader eyebrow={company.name} title={t("justificatifs")}>
        <Input type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="w-44" data-testid="justif-filter-month" />
        <NativeSelect value={status} onChange={(e) => setStatus(e.target.value)} data-testid="justif-filter-status"><option value="">{t("all")}</option><option value="new">{t("justif_new")}</option><option value="processed">{t("justif_processed")}</option></NativeSelect>
        {rw && can("purchases", true) && <ReceiptScan />}
      </PageHeader>
      <p className="text-xs text-slate-500">{t("justif_help")}</p>
      {rw && <UploadPanel onDone={load} />}
      <Panel title={<>{t("justificatifs")} {newCount > 0 && <span className="ms-2 rounded-full bg-orange-100 px-2 py-0.5 text-[11px] font-bold text-orange-800" data-testid="justif-new-count">{newCount} {t("justif_new").toLowerCase()}</span>}</>}>
        {!rows ? <Loading /> : rows.length === 0 ? <Empty /> : (
          <TableWrap>
            <thead className="border-b"><tr><Th>{t("justif_file")}</Th><Th>{t("justif_category")}</Th><Th>{t("period")}</Th><Th>{t("justif_by")}</Th><Th>{t("status")}</Th><Th /></tr></thead>
            <tbody>{rows.map((j) => (
              <tr key={j.id} className="border-b border-slate-50" data-testid={`justif-row-${j.name}`}>
                <Td><div className="flex items-center gap-2"><FileText className="h-4 w-4 shrink-0 text-slate-400" /><span className="font-medium truncate max-w-[260px]">{j.name}</span></div>{j.note && <div className="text-xs text-slate-500">{j.note}</div>}</Td>
                <Td className="text-xs">{t(`cat_${j.category}`)}</Td><Td className="num text-xs">{j.month}</Td>
                <Td className="text-xs">{j.uploaded_by}<div className="text-slate-400">{t(`r_${j.uploaded_role}`)} · {dmyTime(j.created_at)}</div></Td>
                <Td>{j.status === "processed"
                  ? <span className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-700"><CheckCircle2 className="h-3.5 w-3.5" />{t("justif_processed")}<span className="font-normal text-slate-400">· {j.processed_by}</span></span>
                  : <span className="rounded-full bg-orange-50 px-2 py-0.5 text-xs font-semibold text-orange-800">{t("justif_new")}</span>}</Td>
                <Td className="text-end whitespace-nowrap">
                  <Button size="sm" variant="ghost" onClick={() => setOpenId(j.id)} data-testid={`justif-comments-${j.name}`}><MessageSquare className="h-4 w-4" />{j.comments?.length ? <span className="ms-1 text-xs font-bold text-teal-700">{j.comments.length}</span> : null}</Button>
                  <Button size="sm" variant="ghost" onClick={() => download(`/files/${j.file_id}`, j.name, j.content_type === "application/pdf")} data-testid={`justif-download-${j.name}`}><FileDown className="h-4 w-4" /></Button>
                  {j.document_id ? <Button size="sm" variant="link" asChild data-testid={`justif-open-doc-${j.name}`}><Link to={`/app/documents/${j.document_id}`}>{t(`type_${j.document_type}`)} ↗</Link></Button>
                    : rw && can("purchases", true) && (
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild><Button size="sm" data-testid={`justif-to-doc-${j.name}`}><FilePlus2 className="h-4 w-4 me-1" />{t("justif_to_doc")}</Button></DropdownMenuTrigger>
                      <DropdownMenuContent className="bg-white">
                        {(j.content_type === "application/pdf" || (j.content_type || "").startsWith("image/")) && <DropdownMenuItem onClick={() => toDoc(j, "supplier_invoice", true)} data-testid={`justif-to-doc-ocr-${j.name}`}><Sparkles className="h-4 w-4 me-2 text-teal-600" />{t("ocr_option")}</DropdownMenuItem>}
                        {(j.content_type === "application/pdf" || (j.content_type || "").startsWith("image/")) && <DropdownMenuItem onClick={() => toDoc(j, "expense", true)} data-testid={`justif-to-expense-ocr-${j.name}`}><Sparkles className="h-4 w-4 me-2 text-teal-600" />{t("ocr_expense_option")}</DropdownMenuItem>}
                        <DropdownMenuItem onClick={() => toDoc(j, "supplier_invoice")} data-testid={`justif-to-supplier-invoice-${j.name}`}>{t("type_supplier_invoice")}</DropdownMenuItem>
                        <DropdownMenuItem onClick={() => toDoc(j, "expense")} data-testid={`justif-to-expense-${j.name}`}>{t("type_expense")}</DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  )}
                  {rw && j.status === "new" && !j.document_id && <Button size="sm" variant="outline" onClick={() => act(() => api.patch(`/justificatifs/${j.id}`, { status: "processed" }))} data-testid={`justif-process-${j.name}`}><CheckCircle2 className="h-4 w-4 me-1" />{t("justif_mark")}</Button>}
                  {rw && j.status === "processed" && <Button size="sm" variant="ghost" onClick={() => act(() => api.patch(`/justificatifs/${j.id}`, { status: "new" }))} data-testid={`justif-reopen-${j.name}`}><RotateCcw className="h-4 w-4" /></Button>}
                  {rw && j.status === "new" && j.uploaded_by === me?.user?.email && <Button size="sm" variant="ghost" onClick={() => window.confirm(t("delete")) && act(() => api.delete(`/justificatifs/${j.id}`))} data-testid={`justif-delete-${j.name}`}><Trash2 className="h-4 w-4 text-red-600" /></Button>}
                </Td>
              </tr>))}</tbody>
          </TableWrap>
        )}
      </Panel>
      <CommentsDialog j={rows?.find((r) => r.id === openId)} onClose={() => setOpenId(null)} onSaved={load} />
    </>
  );
}
